import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  apiService,
  BatchVideoSegmentsTarget,
  PromptRun,
  SearchResult,
  SegmentResponse,
  Video,
  VideoSegmentsResponse,
} from '../../services/api';
import { segmentCache } from '../../services/segmentCache';
import { sanitizeRunVideoIds } from '../../utils/promptRunProgress';

const INITIAL_ELIGIBLE_VIDEO_COUNT = 15;
const VIDEO_PAGE_SIZE = 25;
const SEGMENT_PAGE_SIZE = 12;

const targetKey = (videoId: string, runId: string): string => `${videoId}::${runId}`;

const toDurableGalleryVideo = (video: Video): Video => ({
  ...video,
  thumbnail_uri: null,
  thumbnail_available: Boolean(video.thumbnail_gcs_uri),
  gif_uri: null,
  gif_available: Boolean(video.gif_gcs_uri),
});

export const toGallerySearchResult = (
  segment: SegmentResponse,
  video: Video,
  runId: string,
  indexId: string,
): SearchResult => ({
  result_type: 'segment',
  result_id: `gallery:${runId}:${segment.segment_id}`,
  video_id: video.video_id,
  video_name: video.title,
  video_uri: video.video_uri || null,
  segment_id: segment.segment_id,
  start_time: segment.start_time,
  end_time: segment.end_time,
  segment_uri: segment.gcs_uri || null,
  gcs_uri: segment.gcs_uri || null,
  thumbnail_gcs_uri: segment.thumbnail_gcs_uri || null,
  gif_gcs_uri: segment.gif_gcs_uri || null,
  thumbnail_uri: undefined,
  gif_uri: undefined,
  thumbnail_data: undefined,
  gif_data: undefined,
  thumbnail_available: Boolean(segment.thumbnail_gcs_uri),
  gif_available: Boolean(segment.gif_gcs_uri),
  media_type: segment.media_type || (video.media_type as SearchResult['media_type']) || 'video',
  text_content: segment.metadata_text || '',
  metadata_text: segment.metadata_text,
  metadata: segment.metadata,
  similarity_score: null,
  run_id: runId,
  source_run_id: runId,
  prompt_run_id: runId,
  source_index_id: indexId,
  marker: segment.marker,
  extracted_metadata_markers: segment.metadata_markers,
});

interface BrowseGalleryState {
  status: 'idle' | 'loading' | 'success' | 'empty' | 'error';
  membershipSignature: string;
  videos: Video[];
  pendingVideos: Video[];
  results: SearchResult[];
  nextVideoCursor: string | null;
  backendHasMoreVideos: boolean;
  hasMoreVideos: boolean;
  loadedTargetKeys: Set<string>;
  targetCursors: Record<string, string | null>;
  hasMoreByTarget: Record<string, boolean>;
  isLoading: boolean;
  isLoadingMoreVideos: boolean;
  loadingTargetKey: string | null;
  error: string | null;
}

const emptyState = (): BrowseGalleryState => ({
  status: 'idle',
  membershipSignature: '',
  videos: [],
  pendingVideos: [],
  results: [],
  nextVideoCursor: null,
  backendHasMoreVideos: true,
  hasMoreVideos: true,
  loadedTargetKeys: new Set(),
  targetCursors: {},
  hasMoreByTarget: {},
  isLoading: false,
  isLoadingMoreVideos: false,
  loadingTargetKey: null,
  error: null,
});

export const useBrowseGallery = ({
  indexId,
  runs,
  enabled,
}: {
  indexId: string | null;
  runs: PromptRun[];
  enabled: boolean;
}) => {
  const selectedRunIds = useMemo(() => runs.map((run) => run.run_id), [runs]);
  const scopeKey = useMemo(() => (
    indexId ? `${indexId}::${[...selectedRunIds].sort().join(',')}` : ''
  ), [indexId, selectedRunIds]);
  const [state, setState] = useState<BrowseGalleryState>(emptyState);
  const stateRef = useRef(state);
  const requestGenerationRef = useRef(0);
  const videoPageRequestGenerationRef = useRef<number | null>(null);
  const requestedVideoBandsRef = useRef(new Set<string>());
  const queuedVideoBandsRef = useRef(new Set<string>());
  const hydratingVideoBandsRef = useRef(new Set<string>());
  const videoBandFlushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  stateRef.current = state;

  const runMembership = useMemo(() => {
    const membership = new Map<string, string[]>();
    runs.forEach((run) => {
      sanitizeRunVideoIds(run).forEach((videoId) => {
        const existing = membership.get(videoId) || [];
        existing.push(run.run_id);
        membership.set(videoId, existing);
      });
    });
    return membership;
  }, [runs]);
  const membershipSignature = useMemo(() => JSON.stringify(
    Array.from(runMembership.entries())
      .map(([videoId, runIds]) => [videoId, [...runIds].sort()] as const)
      .sort(([left], [right]) => left.localeCompare(right)),
  ), [runMembership]);

  const publish = useCallback((updater: (previous: BrowseGalleryState) => BrowseGalleryState) => {
    setState((previous) => {
      const next = updater(previous);
      stateRef.current = next;
      return next;
    });
  }, []);

  const mergeSegmentGroups = useCallback((
    groups: VideoSegmentsResponse[],
    videos: Video[],
  ) => {
    if (!indexId) return;
    const videoMap = new Map(videos.map((video) => [video.video_id, video]));
    publish((previous) => {
      const videoPreviewById = new Map(groups.map((group) => [group.video_id, group]));
      const refreshVideoPreview = (video: Video): Video => {
        const group = videoPreviewById.get(video.video_id);
        if (!group) return video;
        return {
          ...video,
          thumbnail_gcs_uri: group.thumbnail_gcs_uri || null,
          thumbnail_uri: null,
          thumbnail_available: Boolean(group.thumbnail_gcs_uri),
          gif_gcs_uri: group.gif_gcs_uri || null,
          gif_uri: null,
          gif_available: Boolean(group.gif_gcs_uri),
        };
      };
      const nextResults = [...previous.results];
      const seen = new Set(nextResults.map((result) => `${result.video_id}::${result.run_id}::${result.segment_id}`));
      const nextCursors = { ...previous.targetCursors };
      const nextHasMore = { ...previous.hasMoreByTarget };
      const nextLoaded = new Set(previous.loadedTargetKeys);

      groups.forEach((group) => {
        const runId = group.run_id || '';
        const key = targetKey(group.video_id, runId);
        const video = videoMap.get(group.video_id);
        if (!video || !runId) return;
        (group.segments || []).forEach((segment) => {
          const identity = `${group.video_id}::${runId}::${segment.segment_id}`;
          if (seen.has(identity)) return;
          seen.add(identity);
          nextResults.push(toGallerySearchResult(segment, video, runId, indexId));
        });
        nextCursors[key] = group.next_cursor || null;
        nextHasMore[key] = Boolean(group.has_more);
        nextLoaded.add(key);
      });

      return {
        ...previous,
        videos: previous.videos.map(refreshVideoPreview),
        pendingVideos: previous.pendingVideos.map(refreshVideoPreview),
        results: nextResults,
        loadedTargetKeys: nextLoaded,
        targetCursors: nextCursors,
        hasMoreByTarget: nextHasMore,
      };
    });
  }, [indexId, publish]);

  const fetchSegmentsForVideos = useCallback(async (
    videos: Video[],
    generation: number,
  ) => {
    const targets: BatchVideoSegmentsTarget[] = [];
    videos.forEach((video) => {
      (runMembership.get(video.video_id) || []).forEach((runId) => {
        const key = targetKey(video.video_id, runId);
        if (!stateRef.current.loadedTargetKeys.has(key)) {
          targets.push({ video_id: video.video_id, run_id: runId, limit: SEGMENT_PAGE_SIZE });
        }
      });
    });
    if (!targets.length) return;

    for (let offset = 0; offset < targets.length; offset += 100) {
      const groups = await segmentCache.loadBatchSegments(
        targets.slice(offset, offset + 100),
        {
          requestOwnerKey: `${scopeKey}:${generation}`,
        },
      );
      if (generation !== requestGenerationRef.current) return;
      mergeSegmentGroups(groups, videos);
    }
  }, [mergeSegmentGroups, runMembership, scopeKey]);

  const loadVideoPage = useCallback(async (initial: boolean, generation: number) => {
    if (!enabled || !indexId || runs.length === 0) return;
    if (generation !== requestGenerationRef.current) return;
    const current = stateRef.current;
    if (!initial && (!current.hasMoreVideos || current.isLoading || current.isLoadingMoreVideos)) return;
    if (videoPageRequestGenerationRef.current === generation) return;
    videoPageRequestGenerationRef.current = generation;
    publish((previous) => ({
      ...previous,
      status: initial ? 'loading' : previous.status,
      isLoading: initial,
      isLoadingMoreVideos: !initial,
      error: null,
    }));

    try {
      let cursor = initial ? null : current.nextVideoCursor;
      let backendHasMore = initial ? true : current.backendHasMoreVideos;
      const desired = initial
        ? Math.max(0, INITIAL_ELIGIBLE_VIDEO_COUNT - current.videos.length)
        : INITIAL_ELIGIBLE_VIDEO_COUNT;
      const eligible = current.pendingVideos.slice(0, desired);
      const pendingVideos = current.pendingVideos.slice(desired);
      const existingIds = new Set([
        ...current.videos.map((video) => video.video_id),
        ...current.pendingVideos.map((video) => video.video_id),
      ]);

      while (backendHasMore && eligible.length < desired) {
        const response = await apiService.getVideosInIndex(
          indexId,
          VIDEO_PAGE_SIZE,
          cursor || undefined,
          { view: 'gallery' },
        );
        if (generation !== requestGenerationRef.current) return;
        const pageEligible: Video[] = [];
        response.data.forEach((video) => {
          if (runMembership.has(video.video_id) && !existingIds.has(video.video_id)) {
            existingIds.add(video.video_id);
            pageEligible.push(toDurableGalleryVideo(video));
          }
        });
        const availableSlots = Math.max(0, desired - eligible.length);
        eligible.push(...pageEligible.slice(0, availableSlots));
        pendingVideos.push(...pageEligible.slice(availableSlots));
        const previousCursor = cursor;
        cursor = response.pagination.next_cursor || null;
        backendHasMore = Boolean(
          response.pagination.has_more
          && cursor
          && cursor !== previousCursor,
        );
      }

      const allVideos = [...current.videos, ...eligible];
      publish((previous) => ({
        ...previous,
        status: allVideos.length > 0 ? 'success' : 'empty',
        membershipSignature,
        videos: allVideos,
        pendingVideos,
        nextVideoCursor: cursor,
        backendHasMoreVideos: backendHasMore,
        hasMoreVideos: pendingVideos.length > 0 || backendHasMore,
      }));
      if (generation !== requestGenerationRef.current) return;
      publish((previous) => ({
        ...previous,
        isLoading: false,
        isLoadingMoreVideos: false,
      }));
    } catch (error: any) {
      if (generation !== requestGenerationRef.current) return;
      publish((previous) => ({
        ...previous,
        status: 'error',
        isLoading: false,
        isLoadingMoreVideos: false,
        error: error?.message || 'Unable to load the index gallery.',
      }));
    } finally {
      if (videoPageRequestGenerationRef.current === generation) {
        videoPageRequestGenerationRef.current = null;
      }
    }
  }, [enabled, indexId, membershipSignature, publish, runMembership, runs.length]);

  useLayoutEffect(() => {
    requestGenerationRef.current += 1;
    requestedVideoBandsRef.current.clear();
    queuedVideoBandsRef.current.clear();
    hydratingVideoBandsRef.current.clear();
    if (videoBandFlushTimerRef.current) {
      clearTimeout(videoBandFlushTimerRef.current);
      videoBandFlushTimerRef.current = null;
    }
    const cancelDeferredBandHydration = () => {
      requestGenerationRef.current += 1;
      queuedVideoBandsRef.current.clear();
      hydratingVideoBandsRef.current.clear();
      if (videoBandFlushTimerRef.current) {
        clearTimeout(videoBandFlushTimerRef.current);
        videoBandFlushTimerRef.current = null;
      }
    };
    if (!enabled) {
      const next = emptyState();
      stateRef.current = next;
      setState(next);
      return cancelDeferredBandHydration;
    }
    if (!scopeKey || !indexId || runs.length === 0) {
      const next = { ...emptyState(), status: 'empty' as const };
      stateRef.current = next;
      setState(next);
      return cancelDeferredBandHydration;
    }
    const generation = requestGenerationRef.current;
    const next: BrowseGalleryState = {
      ...emptyState(),
      status: 'loading',
      membershipSignature,
      isLoading: true,
    };
    stateRef.current = next;
    setState(next);
    void loadVideoPage(true, generation);
    return cancelDeferredBandHydration;
  // Scope and exact membership are the reset boundaries. Callback identities are refsafe.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, membershipSignature, scopeKey]);

  const loadMoreVideos = useCallback(
    () => loadVideoPage(false, requestGenerationRef.current),
    [loadVideoPage],
  );

  const ensureVideoBands = useCallback((videoId: string) => {
    if (!videoId || !enabled || !indexId || runs.length === 0) return;
    requestedVideoBandsRef.current.add(videoId);
    const hasUnloadedBand = (runMembership.get(videoId) || []).some(
      (runId) => !stateRef.current.loadedTargetKeys.has(targetKey(videoId, runId)),
    );
    if (!hasUnloadedBand || hydratingVideoBandsRef.current.has(videoId)) return;
    queuedVideoBandsRef.current.add(videoId);
    if (videoBandFlushTimerRef.current) return;

    // Intersection callbacks for adjacent rows arrive in the same task. A
    // zero-delay flush combines them into one target batch without eagerly
    // hydrating the remaining offscreen row shells.
    videoBandFlushTimerRef.current = setTimeout(() => {
      videoBandFlushTimerRef.current = null;
      const generation = requestGenerationRef.current;
      const videoIds = Array.from(queuedVideoBandsRef.current);
      queuedVideoBandsRef.current.clear();
      const videosById = new Map(stateRef.current.videos.map((video) => [video.video_id, video]));
      const videosToHydrate = videoIds.flatMap((queuedVideoId) => {
        const video = videosById.get(queuedVideoId);
        if (!video || hydratingVideoBandsRef.current.has(queuedVideoId)) return [];
        hydratingVideoBandsRef.current.add(queuedVideoId);
        return [video];
      });
      if (videosToHydrate.length === 0) return;
      void fetchSegmentsForVideos(videosToHydrate, generation)
        .catch((error: any) => {
          if (generation !== requestGenerationRef.current) return;
          publish((previous) => ({
            ...previous,
            error: error?.message || 'Unable to load gallery segments.',
          }));
        })
        .finally(() => {
          if (generation === requestGenerationRef.current) {
            videosToHydrate.forEach((video) => hydratingVideoBandsRef.current.delete(video.video_id));
          }
        });
    }, 0);
  }, [enabled, fetchSegmentsForVideos, indexId, publish, runMembership, runs.length]);

  const loadMoreBand = useCallback(async (videoId: string, runId: string) => {
    const key = targetKey(videoId, runId);
    const cursor = stateRef.current.targetCursors[key];
    if (!cursor || stateRef.current.loadingTargetKey) return;
    const generation = requestGenerationRef.current;
    publish((previous) => ({ ...previous, loadingTargetKey: key }));
    try {
      const groups: VideoSegmentsResponse[] = await segmentCache.loadBatchSegments(
        [{
          video_id: videoId,
          run_id: runId,
          cursor,
          limit: SEGMENT_PAGE_SIZE,
        }],
        {
          requestOwnerKey: `${scopeKey}:${generation}`,
        },
      );
      if (generation !== requestGenerationRef.current) return;
      mergeSegmentGroups(groups, stateRef.current.videos);
    } catch (error: any) {
      if (generation === requestGenerationRef.current) {
        publish((previous) => ({ ...previous, error: error?.message || 'Unable to load more segments.' }));
      }
    } finally {
      if (generation === requestGenerationRef.current) {
        publish((previous) => ({ ...previous, loadingTargetKey: null }));
      }
    }
  }, [mergeSegmentGroups, publish, scopeKey]);

  const retryGallery = useCallback(async () => {
    if (!enabled || !indexId || runs.length === 0) return;
    const generation = ++requestGenerationRef.current;
    const next: BrowseGalleryState = {
      ...emptyState(),
      status: 'loading',
      membershipSignature,
      isLoading: true,
    };
    stateRef.current = next;
    setState(next);
    await loadVideoPage(true, generation);
  }, [enabled, indexId, loadVideoPage, membershipSignature, runs.length]);

  return {
    ...state,
    scopeKey,
    loadMoreVideos,
    ensureVideoBands,
    loadMoreBand,
    retryGallery,
  };
};
